import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { createArea, fetchAreas } from '@/lib/api';
import { useOrgStore } from '@/stores/org.store';
import { IconPicker } from '@/components/ui/icon-picker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}

export function CreateAreaDialog({ open, onOpenChange, orgId }: Props) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'persistent' | 'project'>('persistent');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [icon, setIcon] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setName('');
    setType('persistent');
    setVisibility('public');
    setIcon(null);
    setDescription('');
    setError('');
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setCreating(true);
    setError('');
    try {
      await createArea(orgId, {
        name: trimmed,
        type,
        visibility,
        ...(icon ? { icon } : {}),
      });
      // Refresh areas in store
      const data = await fetchAreas(orgId);
      useOrgStore.getState().setAreas(data.areas);
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <Dialog.Title className="text-lg font-semibold">
            Create room
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add a new area for your team to communicate in.
          </Dialog.Description>

          <div className="mt-4 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Name + Icon */}
            <div className="flex items-end gap-3">
              <IconPicker value={icon} onChange={setIcon} />
              <div className="flex-1">
                <label htmlFor="area-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  id="area-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Design, Engineering..."
                  maxLength={100}
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            {/* Type toggle */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setType('persistent')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    type === 'persistent'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Persistent
                </button>
                <button
                  type="button"
                  onClick={() => setType('project')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    type === 'project'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Project
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {type === 'persistent' ? 'Permanent area for ongoing topics.' : 'Temporary area for time-bound projects.'}
              </p>
            </div>

            {/* Visibility toggle */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Visibility</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVisibility('public')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    visibility === 'public'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Public
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility('private')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    visibility === 'private'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Private
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {visibility === 'public' ? 'Anyone in the org can find and join.' : 'Invite-only, hidden from non-members.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <button className="rounded-lg border border-gray-300 px-4 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  Cancel
                </button>
              </Dialog.Close>
              <button
                onClick={handleCreate}
                disabled={creating || !name.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create room'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
